import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  JustTypeToDisableTypeScriptError,
  ResourcesGridResourcePayloadType,
  ResourcesGridSliceInterface,
} from "./utils/types/common";
import {
  ResourcesGridCleanCurrentDetails,
  ResourcesGridCleanProcess,
} from "@/store/resources-grid/utils/types/clean";
import { ResourceType } from "@/services/cloudchipr.api";
import { MetricsKey } from "@/components/pages/common/resources-ag-grid/utils/metrics/metricsTypes";

const initialState: ResourcesGridSliceInterface = {
  grids: null,

  currentCleanDetails: null,

  cleanProcesses: [],
};

export const resourcesGridSlice = createSlice({
  name: "resourcesGrid",
  initialState: initialState,
  reducers: {
    addNewGridInResourcesGridSlice: (
      state,
      action: PayloadAction<
        Exclude<ResourcesGridSliceInterface["grids"], null>
      >,
    ) => {
      state.grids = {
        ...(action.payload as unknown as JustTypeToDisableTypeScriptError),
        ...state.grids,
      };
    },
    removeGridByIdResourcesGridSlice: (
      state,
      action: PayloadAction<string>,
    ) => {
      const newGrids = { ...state.grids };

      delete newGrids[action.payload];

      state.grids = newGrids;
    },

    setGridResourceSelectionByGridId: (
      state,
      action: PayloadAction<{
        gridId: string;
        selection: string[];
        parentRowId: string;
        resourceType: ResourceType;
      }>,
    ) => {
      const { gridId, selection, parentRowId, resourceType } = action.payload;

      if (state.grids?.[gridId]) {
        if (!state.grids[gridId].selection) {
          state.grids[gridId].selection = {};
        }

        if (!state.grids[gridId].selection[resourceType]) {
          state.grids[gridId].selection[resourceType] = {};
        }

        state.grids[gridId].selection[resourceType] = {
          ...state.grids[gridId].selection[resourceType],
          [parentRowId]: selection,
        };
      }
    },

    setGridResourceDataPayload: (
      state,
      action: PayloadAction<{
        gridId: string;
        payload: ResourcesGridResourcePayloadType;
      }>,
    ) => {
      const { gridId, payload } = action.payload;

      if (state.grids?.[gridId]) {
        // @ts-expect-error | check JustTypeToDisableTypeScriptError
        state.grids[gridId].resourceDataPayload =
          payload as unknown as JustTypeToDisableTypeScriptError;
      }
    },

    setGridDetailRowsHeights: (
      state,
      action: PayloadAction<{
        gridId: string;
        rowId: string;
        height: number;
      }>,
    ) => {
      const { gridId, height, rowId } = action.payload;

      if (state.grids?.[gridId]) {
        if (!state.grids[gridId].detailsRowHeights) {
          state.grids[gridId].detailsRowHeights = {};
        }

        state.grids[gridId].detailsRowHeights = {
          ...state.grids[gridId].detailsRowHeights,
          [rowId]: height,
        };
      }
    },

    setGridResourceMetricFrequency: (
      state,
      action: PayloadAction<{
        gridId: string;
        resourceType: ResourceType;
        metricKey: MetricsKey;
        frequency: string;
      }>,
    ) => {
      const { gridId, metricKey, frequency, resourceType } = action.payload;

      if (state.grids?.[gridId]) {
        if (!state.grids[gridId].metricsFrequencies) {
          state.grids[gridId].metricsFrequencies = {};
        }

        if (!state.grids[gridId].metricsFrequencies[resourceType]) {
          state.grids[gridId].metricsFrequencies[resourceType] = {};
        }

        state.grids[gridId].metricsFrequencies[resourceType][metricKey] =
          frequency;
      }
    },

    setGridResourceListingPriceFrequency: (
      state,
      action: PayloadAction<{
        gridId: string;
        resourceType: ResourceType;
        frequency: string;
      }>,
    ) => {
      const { gridId, frequency, resourceType } = action.payload;

      if (state.grids?.[gridId]) {
        state.grids[gridId].listingPriceFrequencies ??= {};

        state.grids[gridId].listingPriceFrequencies[resourceType] = frequency;
      }
    },

    // clean ========================================================
    setResourceGridCurrentCleanDetails: (
      state,
      action: PayloadAction<ResourcesGridCleanCurrentDetails | null>,
    ) => {
      state.currentCleanDetails = action.payload;
    },

    setResourcesGridCleanProcesses: (
      state,
      action: PayloadAction<ResourcesGridCleanProcess[]>,
    ) => {
      const processes = state.cleanProcesses;

      state.cleanProcesses = [...processes, ...action.payload];
    },

    deleteResourcesGridCleanProcessByExecutionId: (
      state,
      action: PayloadAction<string>,
    ) => {
      state.cleanProcesses = state.cleanProcesses.filter(
        (process) => process.executionId === action.payload,
      );
    },
  },
});

export const {
  addNewGridInResourcesGridSlice,
  removeGridByIdResourcesGridSlice,
  setGridResourceSelectionByGridId,
  setGridResourceDataPayload,
  setGridDetailRowsHeights,
  setGridResourceMetricFrequency,
  setGridResourceListingPriceFrequency,

  // clean ========================================================

  setResourceGridCurrentCleanDetails,
  setResourcesGridCleanProcesses,
  deleteResourcesGridCleanProcessByExecutionId,
} = resourcesGridSlice.actions;

export default resourcesGridSlice.reducer;
